/**
 * Illustrative regional comparison figures for UI development only.
 * Replace with live aggregates from the ministry backend when wiring this report.
 */

export interface NationalBenchmark {
  reportingYear: string;
  totalSchools: number;
  totalStudents: number;
  /** Student-weighted national average tuition (USD). */
  weightedAvgTuitionUsd: number;
  /** Share of budgets submitted on time (illustrative). */
  budgetSubmittedRatePct: number;
  /** Illustrative compliance rate across submitted budgets. */
  complianceRatePct: number;
}

export interface GovernorateComparisonRow {
  governorateId: string;
  governorateName: string;
  schoolsCount: number;
  studentsCount: number;
  avgTuitionUsdWeighted: number;
  minTuitionUsd: number;
  maxTuitionUsd: number;
  /** Illustrative share of budgets submitted for the period. */
  budgetSubmittedRatePct: number;
  /** Illustrative compliance rate for the governorate. */
  complianceRatePct: number;
}

/** Classification dimension used for the governorate × band breakdown (illustrative labels). */
export type BandKind = 'enrollment' | 'tuition' | 'parent';

/**
 * One slice: governorate × single band. All numeric fields are dummy values for UI only.
 */
export interface GovernorateBandSliceRow {
  governorateId: string;
  governorateName: string;
  bandKind: BandKind;
  bandId: string;
  /** Ministry-style band label (illustrative). */
  bandLabel: string;
  studentsCount: number;
  schoolsCount: number;
  /** Share of students in this governorate that fall in this band (0–100). */
  studentShareOfGovernoratePct: number;
  avgTuitionUsdWeighted: number;
  budgetSubmittedRatePct: number;
  complianceRatePct: number;
}

const ENROLLMENT_BAND_DEF: { id: string; label: string }[] = [
  { id: 'enr_s', label: 'School size — small (under 350 students)' },
  { id: 'enr_m', label: 'School size — medium (350–599)' },
  { id: 'enr_l', label: 'School size — large (600–799)' },
  { id: 'enr_xl', label: 'School size — very large (800 and above)' },
];

const TUITION_BAND_DEF: { id: string; label: string }[] = [
  { id: 'tu_lo', label: 'Tuition cluster — low' },
  { id: 'tu_ml', label: 'Tuition cluster — mid-low' },
  { id: 'tu_mh', label: 'Tuition cluster — mid-high' },
  { id: 'tu_hi', label: 'Tuition cluster — upper' },
];

const PARENT_BAND_DEF: { id: string; label: string }[] = [
  { id: 'pf_l1', label: 'Parent fee band — L1' },
  { id: 'pf_l2', label: 'Parent fee band — L2' },
  { id: 'pf_l3', label: 'Parent fee band — L3' },
  { id: 'pf_l4', label: 'Parent fee band — L4' },
];

/** Deterministic student-share weights per governorate (sum = 1) for dummy split. */
function studentBandWeights(governorateId: string): number[] {
  let seed = 0;
  for (let i = 0; i < governorateId.length; i++) seed += governorateId.charCodeAt(i);
  const base = [0.14, 0.26, 0.36, 0.24];
  const jitter = ((seed % 7) - 3) * 0.01;
  const w = base.map((b, i) => Math.max(0.08, b + jitter * (i % 2 === 0 ? 1 : -1)));
  const sum = w.reduce((a, b) => a + b, 0);
  return w.map((x) => x / sum);
}

function schoolBandWeights(governorateId: string): number[] {
  const sw = studentBandWeights(governorateId);
  return sw.map((_, i) => {
    const t = sw[i]! + (i === 1 ? 0.04 : i === 3 ? -0.02 : 0);
    return Math.max(0.06, t);
  }).map((v, _, arr) => v / arr.reduce((a, b) => a + b, 0));
}

function buildSlicesForKind(
  rows: GovernorateComparisonRow[],
  kind: BandKind,
  defs: { id: string; label: string }[]
): GovernorateBandSliceRow[] {
  const out: GovernorateBandSliceRow[] = [];
  for (const g of rows) {
    const sw = studentBandWeights(g.governorateId);
    const scw = schoolBandWeights(g.governorateId);
    defs.forEach((def, i) => {
      const share = sw[i] ?? 0;
      const students = Math.max(1, Math.round(g.studentsCount * share));
      const schools = Math.max(1, Math.round(g.schoolsCount * (scw[i] ?? share)));
      const tuitionJitter = 0.88 + (i * 0.04) + (parseInt(g.governorateId, 10) % 3) * 0.02;
      const avgTuition = Math.round(g.avgTuitionUsdWeighted * tuitionJitter);
      const subVar = (parseInt(g.governorateId, 10) + i) % 5;
      const submitted = Math.min(99.5, g.budgetSubmittedRatePct - 1.2 + subVar * 0.35);
      const compliance = Math.min(98.5, g.complianceRatePct - 1.5 + subVar * 0.4);
      out.push({
        governorateId: g.governorateId,
        governorateName: g.governorateName,
        bandKind: kind,
        bandId: def.id,
        bandLabel: def.label,
        studentsCount: students,
        schoolsCount: schools,
        studentShareOfGovernoratePct: Math.round(share * 1000) / 10,
        avgTuitionUsdWeighted: avgTuition,
        budgetSubmittedRatePct: Math.round(submitted * 10) / 10,
        complianceRatePct: Math.round(compliance * 10) / 10,
      });
    });
  }
  return out;
}

function buildAllBandSlices(rows: GovernorateComparisonRow[]): Record<BandKind, GovernorateBandSliceRow[]> {
  return {
    enrollment: buildSlicesForKind(rows, 'enrollment', ENROLLMENT_BAND_DEF),
    tuition: buildSlicesForKind(rows, 'tuition', TUITION_BAND_DEF),
    parent: buildSlicesForKind(rows, 'parent', PARENT_BAND_DEF),
  };
}

/** Shown in the UI so figures are never mistaken for production data. */
export const REGIONAL_COMPARISONS_ILLUSTRATIVE_AS_OF = '1 May 2026';

const YEAR_MULTIPLIER: Record<string, number> = {
  '2024': 0.94,
  '2025': 0.97,
  '2026': 1,
};

const BASE_ROWS: GovernorateComparisonRow[] = [
  {
    governorateId: '1',
    governorateName: 'Beirut',
    schoolsCount: 412,
    studentsCount: 118_400,
    avgTuitionUsdWeighted: 6_280,
    minTuitionUsd: 2_100,
    maxTuitionUsd: 14_200,
    budgetSubmittedRatePct: 96.2,
    complianceRatePct: 91.4,
  },
  {
    governorateId: '2',
    governorateName: 'Mount Lebanon',
    schoolsCount: 1_842,
    studentsCount: 512_600,
    avgTuitionUsdWeighted: 5_040,
    minTuitionUsd: 1_650,
    maxTuitionUsd: 12_800,
    budgetSubmittedRatePct: 94.1,
    complianceRatePct: 88.7,
  },
  {
    governorateId: '3',
    governorateName: 'North',
    schoolsCount: 628,
    studentsCount: 186_200,
    avgTuitionUsdWeighted: 3_920,
    minTuitionUsd: 1_420,
    maxTuitionUsd: 9_600,
    budgetSubmittedRatePct: 89.5,
    complianceRatePct: 84.2,
  },
  {
    governorateId: '4',
    governorateName: 'South',
    schoolsCount: 534,
    studentsCount: 162_800,
    avgTuitionUsdWeighted: 4_180,
    minTuitionUsd: 1_380,
    maxTuitionUsd: 10_400,
    budgetSubmittedRatePct: 90.8,
    complianceRatePct: 85.6,
  },
  {
    governorateId: '5',
    governorateName: 'Bekaa',
    schoolsCount: 356,
    studentsCount: 98_400,
    avgTuitionUsdWeighted: 3_560,
    minTuitionUsd: 1_200,
    maxTuitionUsd: 8_900,
    budgetSubmittedRatePct: 87.2,
    complianceRatePct: 82.1,
  },
  {
    governorateId: '6',
    governorateName: 'Nabatieh',
    schoolsCount: 198,
    studentsCount: 54_200,
    avgTuitionUsdWeighted: 3_740,
    minTuitionUsd: 1_260,
    maxTuitionUsd: 9_100,
    budgetSubmittedRatePct: 88.9,
    complianceRatePct: 83.4,
  },
];

function scaleRow(r: GovernorateComparisonRow, m: number): GovernorateComparisonRow {
  const round2 = (n: number) => Math.round(n * m * 100) / 100;
  return {
    ...r,
    schoolsCount: Math.max(1, Math.round(r.schoolsCount * m)),
    studentsCount: Math.max(1, Math.round(r.studentsCount * m)),
    avgTuitionUsdWeighted: Math.round(r.avgTuitionUsdWeighted * m),
    minTuitionUsd: Math.round(r.minTuitionUsd * m),
    maxTuitionUsd: Math.round(r.maxTuitionUsd * m),
    budgetSubmittedRatePct: round2(Math.min(99.9, r.budgetSubmittedRatePct * (0.98 + m * 0.02))),
    complianceRatePct: round2(Math.min(99.5, r.complianceRatePct * (0.98 + m * 0.02))),
  };
}

function buildBenchmark(rows: GovernorateComparisonRow[], year: string): NationalBenchmark {
  const totalSchools = rows.reduce((s, r) => s + r.schoolsCount, 0);
  const totalStudents = rows.reduce((s, r) => s + r.studentsCount, 0);
  const weightedAvgTuitionUsd =
    totalStudents > 0
      ? rows.reduce((acc, r) => acc + r.avgTuitionUsdWeighted * r.studentsCount, 0) / totalStudents
      : 0;
  const budgetSubmittedRatePct =
    totalSchools > 0
      ? rows.reduce((acc, r) => acc + r.budgetSubmittedRatePct * r.schoolsCount, 0) / totalSchools
      : 0;
  const complianceRatePct =
    totalSchools > 0
      ? rows.reduce((acc, r) => acc + r.complianceRatePct * r.schoolsCount, 0) / totalSchools
      : 0;
  return {
    reportingYear: year,
    totalSchools,
    totalStudents,
    weightedAvgTuitionUsd: Math.round(weightedAvgTuitionUsd),
    budgetSubmittedRatePct: Math.round(budgetSubmittedRatePct * 10) / 10,
    complianceRatePct: Math.round(complianceRatePct * 10) / 10,
  };
}

export function getMockRegionalDataset(year: string): {
  benchmark: NationalBenchmark;
  rows: GovernorateComparisonRow[];
  governorateOptions: { id: string; name: string }[];
  /** Governorate × band slices (illustrative); same year scaling as `rows`. */
  bandSlices: Record<BandKind, GovernorateBandSliceRow[]>;
} {
  const m = YEAR_MULTIPLIER[year] ?? YEAR_MULTIPLIER['2026']!;
  const rows = BASE_ROWS.map((r) => scaleRow(r, m));
  const benchmark = buildBenchmark(rows, year);
  const governorateOptions = rows.map((r) => ({ id: r.governorateId, name: r.governorateName }));
  const bandSlices = buildAllBandSlices(rows);
  return { benchmark, rows, governorateOptions, bandSlices };
}

export const BAND_KIND_LABELS: Record<BandKind, string> = {
  enrollment: 'School size (enrollment) bands',
  tuition: 'Tuition cluster bands',
  parent: 'Parent fee bands',
};

export const REPORTING_YEAR_OPTIONS = ['2026', '2025', '2024'] as const;
